
import React, { useState } from 'react';
import { 
  Sheet, 
  SheetContent, 
  SheetHeader, 
  SheetTitle,
  SheetTrigger
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Family, Parent, Student } from '@/types/models';
import FamilyInformation from './sidebar/FamilyInformation';
import ParentsSection from './sidebar/ParentsSection';
import StudentsSection from './sidebar/StudentsSection';
import { X, Users, GitBranch } from 'lucide-react';
import EditStudentDialog from '@/components/students/EditStudentDialog';

interface FamilyDetailsSidebarProps {
  family: Family;
  parents: Parent[];
  students: Student[];
  isOpen: boolean;
  onClose: () => void;
  onSendInvite: (parentId: string) => void;
  onEditParent: (parent: Parent) => void;
  onAddStudent: (familyId: string) => void;
  onAddParent: () => void;
  onViewFamilyTree?: () => void;
}

const FamilyDetailsSidebar: React.FC<FamilyDetailsSidebarProps> = ({
  family,
  parents,
  students,
  isOpen,
  onClose,
  onSendInvite,
  onEditParent,
  onAddStudent,
  onAddParent,
  onViewFamilyTree
}) => {
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [isEditStudentDialogOpen, setIsEditStudentDialogOpen] = useState(false);
  
  if (!family) return null;

  // This is the actual handleSendInvite function that will be passed to ParentsSection
  const handleSendInvite = (parentId: string) => {
    onSendInvite(parentId);
  };

  // This is the actual handleEditParent function that will be passed to ParentsSection
  const handleEditParent = (parent: Parent) => {
    onEditParent(parent);
  };

  // This is the actual handleAddStudent function that will be passed to StudentsSection
  const handleAddStudent = () => {
    onAddStudent(family.id);
  };
  
  const handleEditStudent = (student: Student) => {
    setEditingStudent(student);
    setIsEditStudentDialogOpen(true);
  };

  const handleStudentUpdated = (updatedStudent: Student) => {
    setIsEditStudentDialogOpen(false);
    // The parent component will handle refreshing the data
  };

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent className="sm:max-w-md overflow-y-auto">
          <SheetHeader className="relative mb-6">
            <SheetTitle className="pr-10">
              {family.name} Family
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </SheetHeader>

          <div className="space-y-8">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Family Information</h3>
              <FamilyInformation family={family} />
              
              {onViewFamilyTree && (
                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={onViewFamilyTree}
                  >
                    <GitBranch className="mr-2 h-4 w-4" />
                    View Family Tree
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Parents</h3>
              </div>
              <ParentsSection 
                parents={parents} 
                onAddParent={onAddParent}
                onSendInvite={handleSendInvite}
                onEditParent={handleEditParent}
              />
            </div>

            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Students</h3>
              </div>
              <StudentsSection 
                students={students} 
                onAddStudent={handleAddStudent}
                onEditStudent={handleEditStudent}
              />
            </div>
          </div>
        </SheetContent>
      </Sheet>
      
      {editingStudent && (
        <EditStudentDialog
          student={editingStudent}
          open={isEditStudentDialogOpen}
          onOpenChange={setIsEditStudentDialogOpen}
          onUpdated={handleStudentUpdated}
        />
      )}
    </>
  );
};

export default FamilyDetailsSidebar;
